import { AlertTriangle, RefreshCw, LogIn } from 'lucide-react'
import { Button, Card, CardSubtitle, CardTitle } from '@/shared/components/ui'

interface QueryErrorScreenProps {
  /** Error de la query (de `useQuery`). Acepta `unknown` porque Apollo no estrecha el tipo. */
  error?: unknown
  /** Acción de reintento (típicamente `refetch` de Apollo o `window.location.reload`). */
  onRetry?: () => void
  /** Acción opcional secundaria (ej. "Ir al login"). */
  onSecondary?: () => void
  secondaryLabel?: string
  /** Título mostrado. Default: "No pudimos cargar tu información". */
  title?: string
  /** Subtítulo / descripción. Si no se pasa, se infiere del error. */
  subtitle?: string
}

/**
 * Detecta si el error es de red (gateway caído, CORS, DNS) vs un error de
 * datos. Útil para mensajes más específicos.
 */
function isNetworkError(error: unknown): boolean {
  if (!error) return false
  if (error instanceof TypeError && /fetch|network/i.test(error.message)) return true
  const message = (error as { message?: string }).message ?? ''
  return /network|fetch|cors|failed to fetch/i.test(message)
}

function defaultSubtitle(error: unknown): string {
  if (isNetworkError(error)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión y vuelve a intentar. Si el problema persiste, contacta a soporte.'
  }
  const message = (error as { message?: string })?.message
  return message
    ? `Detalle técnico: ${message}`
    : 'Algo salió mal al cargar la información. Intenta de nuevo.'
}

/**
 * Pantalla de error para fallos de query que NO son de auth.
 *
 * Úsala en componentes que envuelven queries (RequireAuth, pages de feature)
 * cuando `useQuery` devuelve `error` y no es un 401 (esos los maneja el errorLink
 * de Apollo redirigiendo a identity).
 *
 * Es distinta del `AppErrorBoundary`: el boundary captura errores THROWN en el
 * render, mientras que Apollo errors viven en el `error` state y no se lanzan.
 */
export function QueryErrorScreen({
  error,
  onRetry,
  onSecondary,
  secondaryLabel,
  title = 'No pudimos cargar tu información',
  subtitle,
}: QueryErrorScreenProps) {
  const finalSubtitle = subtitle ?? defaultSubtitle(error)
  const handleRetry = onRetry ?? (() => window.location.reload())

  return (
    <div className="grid min-h-screen place-items-center bg-surface p-6">
      <Card className="w-full max-w-lg space-y-4 text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-warning-soft text-warning">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <CardTitle className="font-serif text-2xl font-normal">{title}</CardTitle>
        <CardSubtitle className="text-sm">{finalSubtitle}</CardSubtitle>
        <div className="flex flex-wrap justify-center gap-2 pt-2">
          <Button onClick={handleRetry} variant="primary">
            <RefreshCw className="h-4 w-4" />
            Reintentar
          </Button>
          {onSecondary && (
            <Button onClick={onSecondary} variant="outline">
              <LogIn className="h-4 w-4" />
              {secondaryLabel ?? 'Ir al login'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  )
}
